import React, { useEffect, useState } from 'react';
import { Field, RichText } from '@sitecore-jss/sitecore-jss-react';

import './index.scss';
import { getToken, Logout } from '../../utils/authHelper';
import { Navigate } from 'react-router-dom';
import config from '../../temp/config';

type SignoutPageProps = {
  fields: {
    RichTextField: Field<string>;
    LogoutRedirectUri: Field<string>;
  };
};

const openInNewTabAndRefresh = (url: string | URL | undefined) => {
  // Open the URL in a new tab without shifting focus
  window.open(url, '_blank', 'noopener,noreferrer');
};

const triggerFrontChannelLogout = (
  targets: { lobKey?: string; frontChannelLogoutUrl?: string }[]
) => {
  targets.forEach((target) => {
    if (!target?.lobKey || !target.frontChannelLogoutUrl) {
      console.warn('Skipping invalid SSO logout target:', target);
      return;
    }

    if (target.lobKey.toLowerCase() === 'webstore') {
      return;
    }

    const iframe = document.createElement('iframe');

    iframe.style.display = 'none';
    iframe.src = target.frontChannelLogoutUrl;

    document.body.appendChild(iframe);

    const removeLogoutIframe = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    iframe.onload = removeLogoutIframe;
    iframe.onerror = removeLogoutIframe;
  });
};

const logoutSsoSession = async () => {
  const token = getToken();

  if (!token) {
    return [];
  }

  const response = await fetch(`${process.env.REACT_APP_PROFILE_URL}/profile/sso/logout`, {
    method: 'POST',
    headers: new Headers({
      Authorization: `Bearer ${token}`,
      'Ocp-Apim-Subscription-Key': config.subscriptionKey || '',
    }),
  });

  if (!response.ok) {
    throw new Error(`SSO logout failed with status ${response.status}`);
  }

  const result = await response.json();

  return result?.frontChannelLogoutTargets || [];
};

const SignoutPage = ({ fields }: SignoutPageProps): JSX.Element => {
  const [loggedOut, setLoggedOut] = useState(false);

  useEffect(() => {
    const performLogout = async () => {
      try {
        const targets = await logoutSsoSession();
        triggerFrontChannelLogout(targets);
      } catch (error) {
        console.log('SSO logout failed:', error);
      } finally {
        Logout();

        let params = new URLSearchParams(window.location.search);

        let lob = params.get('lob') || '';
        lob = lob === '' ? 'webstore' : lob + ',webstore';

        const domainName = process.env.REACT_APP_B2C_DOMAIN;
        const tenantId = process.env.REACT_APP_B2C_TENANT_ID;
        const policy = process.env.REACT_APP_B2C_POLICY;
        if (tenantId && policy && domainName) {
          const redirectUrl = new URL(fields.LogoutRedirectUri.value);
          redirectUrl.searchParams.set('lob', lob);

          if (params.has('store')) {
            redirectUrl.searchParams.set('store', params.get('store') || '');
          }

          window.location.href =
            `${domainName}/${tenantId}.onmicrosoft.com/${policy}/oauth2/v2.0/logout` +
            `?post_logout_redirect_uri=${encodeURIComponent(redirectUrl.toString())}`;
        } else {
          window.location.href = window.location.origin;
        }
      }
    };
    performLogout();
  }, []);

  return (
    <div className="signoutpage">
      {loggedOut && <Navigate to="/" />}
      <RichText field={fields.RichTextField} />
    </div>
  );
};

export default SignoutPage;
